import { FC } from "react";
import { useTranslation } from "react-i18next";

import { SVG_MUI } from "@ryde/assets";
import {
  HubAssignmentSourceEnum,
  isIncompleteHubPairingBooking,
  isValidPositiveNumber,
  NO_VALUE_DISPLAY,
} from "@ryde/shared";

import { IRenderColumnTemplate } from "../_core";
import { FramerTooltip } from "../../../common";
import { BookingHubCellStyled } from "./booking-hub-cell.styled";
import { TBookingHubRideLinks } from "./parts/booking-hub-cell.models";
import {
  getBookingHubSourceLabelKey,
  parseBookingHubDisplay,
} from "./parts/booking-hub-cell.utils";

type IBookingHubCellTemplateProps = Pick<
  IRenderColumnTemplate,
  "value" | "rowOriginal"
>;

export const BookingHubCellTemplate: FC<IBookingHubCellTemplateProps> = ({
  value,
  rowOriginal,
}) => {
  const { t } = useTranslation();

  const hubDisplay = parseBookingHubDisplay(value);
  const booking = rowOriginal as TBookingHubRideLinks | null;
  const isIncompletePairing = Boolean(
    booking && isIncompleteHubPairingBooking(booking),
  );

  const incompleteBadge = isIncompletePairing ? (
    <span
      className="booking-hub-cell__incomplete"
      aria-label={t("hub.incompletePairing")}
      data-e2e="booking-hub-incomplete-pairing"
    >
      <SVG_MUI.TriangleExclamationSVG className="booking-hub-cell__incomplete-icon" />
    </span>
  ) : null;

  if (!hubDisplay) {
    if (incompleteBadge) {
      return (
        <BookingHubCellStyled>
          <FramerTooltip content={t("hub.incompletePairing")}>
            <span className="booking-hub-cell">{incompleteBadge}</span>
          </FramerTooltip>
        </BookingHubCellStyled>
      );
    }
    return <span>{NO_VALUE_DISPLAY}</span>;
  }

  if (hubDisplay.hubSource === HubAssignmentSourceEnum.Skip) {
    const skipTooltip = isIncompletePairing
      ? `${t("hub.skippedHub")} — ${t("hub.incompletePairing")}`
      : t("hub.skippedHub");

    return (
      <BookingHubCellStyled>
        <FramerTooltip content={skipTooltip}>
          <span className="booking-hub-cell">
            <span
              className={`booking-hub-cell__badge booking-hub-cell__badge--${hubDisplay.hubSource.toLowerCase()}`}
            >
              {t("hub.skippedHub")}
            </span>
            {incompleteBadge}
          </span>
        </FramerTooltip>
      </BookingHubCellStyled>
    );
  }

  if (hubDisplay.hubSource === HubAssignmentSourceEnum.None) {
    if (incompleteBadge) {
      return (
        <BookingHubCellStyled>
          <FramerTooltip content={t("hub.incompletePairing")}>
            <span className="booking-hub-cell">{incompleteBadge}</span>
          </FramerTooltip>
        </BookingHubCellStyled>
      );
    }
    return <span>{NO_VALUE_DISPLAY}</span>;
  }

  const hubName =
    hubDisplay.effectiveHubStationName ||
    (isValidPositiveNumber(hubDisplay.effectiveHubId)
      ? String(hubDisplay.effectiveHubId)
      : NO_VALUE_DISPLAY);

  const sourceLabelKey = getBookingHubSourceLabelKey(hubDisplay.hubSource);

  const isCalculatedHub =
    hubDisplay.hubSource === HubAssignmentSourceEnum.Calculated;

  const tooltipParts = [
    sourceLabelKey ? `${hubName} (${t(sourceLabelKey)})` : hubName,
    isIncompletePairing ? t("hub.incompletePairing") : undefined,
  ].filter(Boolean);

  return (
    <BookingHubCellStyled>
      <FramerTooltip content={tooltipParts.join(" — ")}>
        <span className="booking-hub-cell">
          {isCalculatedHub && (
            <span
              className="booking-hub-cell__calculated-mark"
              aria-label={t("hub.calculatedHub")}
            >
              *
            </span>
          )}

          <SVG_MUI.HubNotRouted4SVG className="hub-icon" />

          <span className="booking-hub-cell__name">{hubName}</span>
          {incompleteBadge}
        </span>
      </FramerTooltip>
    </BookingHubCellStyled>
  );
};
